import { pageAsset } from "@Utils/getStaticAssetPath";

// Images come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("AmbassadorV2");

/**
 * Where a reel's stills live: `reels/<shortcode>.jpg` for the poster and
 * `avatars/<handle>.jpg` for the profile picture, both on the bucket beside the
 * rest of the page's images.
 *
 * Files from earlier creator sets are still on the bucket but no longer
 * referenced — each supplied set replaced the last one entirely.
 */
export const posterFor = (shortcode: string) => asset(`reels/${shortcode}.jpg`);
export const avatarFor = (handle: string) => asset(`avatars/${handle}.jpg`);

export interface Reel {
  /** Instagram shortcode — also the poster filename and the React key. */
  id: string;
  /** First frame, on the bucket. */
  poster?: string;
  /** Instagram handle, without the @. */
  handle?: string;
  /**
   * The creator's profile picture, 150x150 as Instagram serves it. OPTIONAL:
   * some creators serve Instagram's blank placeholder rather than a real
   * picture, and the card drops the avatar rather than show a silhouette.
   */
  avatar?: string;
  /** Follower count as Instagram writes it. A snapshot — see below. */
  followers?: string;
  /** Permalink to the reel. */
  href: string;
  /**
   * Self-hosted mp4. Unset for now — see the note below. When present the card
   * autoplays it muted and looped instead of showing the still.
   */
  videoSrc?: string;
}

const permalink = (code: string) => `https://www.instagram.com/reel/${code}/`;

/**
 * The creator reels in the hero rail, in the order supplied.
 *
 * ── Where this data came from ──────────────────────────────────────────────
 * The permalinks were supplied and nothing else. Instagram's embed endpoint
 * returns no post data to a plain server fetch — the response is their own CSS
 * and JS — so the handle, follower count, avatar and poster frame were read out
 * of each reel's embed AFTER its JavaScript had run, in a headless browser, and
 * the images were downloaded from there. Nothing here is invented.
 *
 * ⚠️  FOLLOWER COUNTS ARE A SNAPSHOT, captured Sep 2026, and they drift. Treat
 * them as of that date, not as a feed.
 *
 * ⚠️  TWO OF THE TEN SUPPLIED REELS WERE DROPPED, leaving eight. `DV0tvl2gTtV`
 * (@jaystravelvlogs) and `DaSazgKRsoO` (@kietphaan) are collab posts with
 * @amberstudent, and Instagram renders a collab embed with no `<video>` element
 * at all — the only images it serves are the two avatars, so there is no first
 * frame to harvest. Both permalinks return 200 and the posts are fine; it is
 * purely how Instagram renders that post type. To put them back, get the source
 * mp4s from the social team (amber co-owns both) and set `videoSrc`.
 *
 * ⚠️  STILL POSTERS, NOT PLAYING VIDEO. `videoSrc` is wired and the card autoplays
 * muted, looped and inline the moment it is set — but Instagram's own video URLs
 * are signed and expire, so they cannot be used. Real playback needs an mp4 per
 * reel from the creators, which is their call to make.
 *
 * ⚠️  RIGHTS. These are the creators' own reels. A poster frame with the handle on
 * it, linking back to the original, is the normal attribution pattern.
 * Self-hosting their video files is a bigger question and needs their say-so
 * before the mp4s go anywhere near the bucket.
 */
export const REELS: Reel[] = [
  {
    id: "DbqEByAx21z",
    poster: posterFor("DbqEByAx21z"),
    handle: "rclangeline",
    avatar: avatarFor("rclangeline"),
    followers: "2K",
    href: permalink("DbqEByAx21z"),
  },
  {
    // Instagram serves the blank placeholder avatar for this one — omitted.
    id: "DaqPIU0h3X4",
    poster: posterFor("DaqPIU0h3X4"),
    handle: "mr_kenling",
    followers: "3K",
    href: permalink("DaqPIU0h3X4"),
  },
  {
    id: "DasCMPcRDG3",
    poster: posterFor("DasCMPcRDG3"),
    handle: "rclangeline",
    avatar: avatarFor("rclangeline"),
    followers: "2K",
    href: permalink("DasCMPcRDG3"),
  },
  {
    id: "DadsqvEpquc",
    poster: posterFor("DadsqvEpquc"),
    handle: "damolaa_x",
    avatar: avatarFor("damolaa_x"),
    followers: "98K",
    href: permalink("DadsqvEpquc"),
  },
  {
    id: "DaQFFNNvA2M",
    poster: posterFor("DaQFFNNvA2M"),
    handle: "itsmeameliaa._",
    avatar: avatarFor("itsmeameliaa._"),
    followers: "4K",
    href: permalink("DaQFFNNvA2M"),
  },
  {
    id: "DZNCO75I2K4",
    poster: posterFor("DZNCO75I2K4"),
    handle: "pretty.ay0_",
    avatar: avatarFor("pretty.ay0_"),
    followers: "1K",
    href: permalink("DZNCO75I2K4"),
  },
  {
    id: "DZMdratIIBP",
    poster: posterFor("DZMdratIIBP"),
    handle: "boss_ellarh",
    avatar: avatarFor("boss_ellarh"),
    followers: "3K",
    href: permalink("DZMdratIIBP"),
  },
  {
    id: "DXl1d0TjGsi",
    poster: posterFor("DXl1d0TjGsi"),
    handle: "dabrien3",
    avatar: avatarFor("dabrien3"),
    // Instagram wrote this one out in full rather than rounding it.
    followers: "9,659",
    href: permalink("DXl1d0TjGsi"),
  },
];
